/**
 * Ordenação por coluna, igual em todo o sistema:
 *  - 1º clique ▲ (crescente), 2º ▼ (decrescente), 3º volta à ordem padrão da lista;
 *  - datas e valores (R$, %, números) pelo valor; texto sem acento/maiúscula; vazios sempre no fim;
 *  - escolha lembrada por pessoa e por lista (localStorage) ou no endereço (listas paginadas pelo servidor).
 */
import React, { isValidElement, useState, type ReactNode } from "react";
import { useSearch } from "./router";

export type SortDir = "asc" | "desc";
export interface SortState { key: string | null; dir: SortDir | null }
export type SortVal = string | number | Date | boolean | null | undefined;

const fold = (s: string) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();

/** Texto visível de um nó React (strings/números dentro de elementos comuns). */
export function nodeText(n: ReactNode): string {
  if (n === null || n === undefined || typeof n === "boolean") return "";
  if (typeof n === "string" || typeof n === "number") return String(n);
  if (Array.isArray(n)) return n.map(nodeText).filter(Boolean).join(" ");
  if (isValidElement(n)) {
    const p = n.props as { children?: ReactNode; title?: string; value?: unknown };
    const inner = nodeText(p.children);
    if (inner) return inner;
    if (typeof p.value === "string" || typeof p.value === "number") return String(p.value);
    return "";
  }
  return "";
}

/** Converte texto exibido em valor ordenável (data, dinheiro, número ou texto). */
export function sortKeyOf(v: SortVal | ReactNode): number | string | null {
  if (v === null || v === undefined || v === "") return null;
  if (v instanceof Date) return v.getTime();
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "boolean") return v ? 1 : 0;
  const s = (typeof v === "string" ? v : nodeText(v as ReactNode)).trim();
  if (!s || s === "—" || s === "-") return null;
  // dd/mm/aaaa [hh:mm]
  let m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:[ ,]+(\d{2}):(\d{2}))?/);
  if (m) return Date.UTC(+m[3]!, +m[2]! - 1, +m[1]!, +(m[4] ?? 0), +(m[5] ?? 0));
  // aaaa-mm-dd
  m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!);
  // R$ 1.234,56 / -1.234,56 / 12,5% / 1234
  const money = s.replace(/R\$/gi, "").replace(/\s/g, "");
  if (/^[-−]?\d{1,3}(\.\d{3})*(,\d+)?%?$/.test(money) || /^[-−]?\d+(,\d+)?%?$/.test(money)) {
    return Number(money.replace(/[−]/, "-").replace(/\./g, "").replace(",", ".").replace("%", ""));
  }
  // "#123 Título" → número
  m = s.match(/^#(\d+)\b/);
  if (m) return Number(m[1]);
  return fold(s);
}

export function compareKeys(a: number | string | null, b: number | string | null, dir: SortDir): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1; // vazios sempre no fim
  if (b === null) return -1;
  let r: number;
  if (typeof a === "number" && typeof b === "number") r = a - b;
  else if (typeof a === "number") r = -1; // números antes de texto
  else if (typeof b === "number") r = 1;
  else r = a.localeCompare(b, "pt-BR", { numeric: true });
  return dir === "asc" ? r : -r;
}

export function sortRows<T>(rows: T[], keyOf: (r: T) => number | string | null, dir: SortDir): T[] {
  const withKeys = rows.map((r, i) => ({ r, k: keyOf(r), i }));
  withKeys.sort((x, y) => compareKeys(x.k, y.k, dir) || x.i - y.i);
  return withKeys.map((x) => x.r);
}

export const nextSort = (cur: SortState, key: string): SortState =>
  cur.key !== key ? { key, dir: "asc" } : cur.dir === "asc" ? { key, dir: "desc" } : { key: null, dir: null };

const LS = "vg.sort.";
/** Ordenação lembrada por pessoa/lista (no navegador). */
export function useStoredSort(id: string): [SortState, (s: SortState) => void] {
  const [s, setS] = useState<SortState>(() => {
    try { const v = JSON.parse(localStorage.getItem(LS + id) ?? "null") as SortState | null; return v?.key ? v : { key: null, dir: null }; } catch { return { key: null, dir: null }; }
  });
  return [s, (v) => { setS(v); try { if (v.key) localStorage.setItem(LS + id, JSON.stringify(v)); else localStorage.removeItem(LS + id); } catch { /* ignore */ } }];
}

/** Ordenação feita pelo servidor, guardada no endereço (?ordem=&dir=). */
export function useUrlSort(): { sort: SortState; set: (s: SortState) => void; params: { sort?: string; dir?: SortDir } } {
  const [search, set] = useSearch();
  const key = search.get("ordem");
  const dir = (search.get("dir") as SortDir | null) ?? (key ? "asc" : null);
  const sort: SortState = { key, dir };
  return {
    sort,
    set: (s) => set({ ordem: s.key, dir: s.key ? s.dir : null, skip: null }),
    params: key ? { sort: key, dir: dir ?? "asc" } : {},
  };
}

/** Ordenação feita pelo servidor, em estado local (telas sem filtros na URL). */
export function useLocalServerSort(id: string): { sort: SortState; set: (s: SortState) => void; params: { sort?: string; dir?: SortDir } } {
  const [sort, set] = useStoredSort(id);
  return { sort, set, params: sort.key ? { sort: sort.key, dir: sort.dir ?? "asc" } : {} };
}

/** Cabeçalho clicável (para tabelas montadas à mão). */
export function SortTh({ label, k, sort, onSort, style, className }: { label: ReactNode; k: string | null; sort: SortState; onSort: (s: SortState) => void; style?: React.CSSProperties; className?: string }): JSX.Element {
  if (!k) return <th style={style} className={className}>{label}</th>;
  const on = sort.key === k;
  return (
    <th style={style} className={`th-sort ${on ? "on" : ""} ${className ?? ""}`} aria-sort={on ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
      onClick={() => onSort(nextSort(sort, k))} title="Clique para ordenar (de novo inverte; 3º clique volta ao padrão)">
      {label}<span className="th-arrow">{on ? (sort.dir === "asc" ? " ▲" : " ▼") : " ↕"}</span>
    </th>
  );
}

/** Para tabelas montadas à mão: ordena as linhas carregadas (lembrado por lista). */
export function useTableSort<T>(id: string, rows: T[] | undefined, accessors: Record<string, (r: T) => SortVal | ReactNode>): { rows: T[]; th: (label: ReactNode, k: string | null, style?: React.CSSProperties) => JSX.Element } {
  const [sort, setSort] = useStoredSort(id);
  const list = rows ?? [];
  const acc = sort.key ? accessors[sort.key] : undefined;
  const sorted = acc && sort.dir ? sortRows(list, (r) => sortKeyOf(acc(r) as SortVal), sort.dir) : list;
  return { rows: sorted, th: (label, k, style) => <SortTh key={k ?? String(label)} label={label} k={k && accessors[k] ? k : null} sort={sort} onSort={setSort} style={style} /> };
}
